import { useState } from 'react'

import { BlankSlate, Tabs } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  CurrencySelectorCurrencyFragment,
  ExpenseDetailPageDocument,
  ExpensesRootPageQuery,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import { useExpensableDetail } from 'modules/expenses/hooks/useExpensableDetail'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import useLegalEntities from 'modules/expenses/hooks/useLegalEntities'
import {
  getOrganizationForLegalEntity,
  useTaxAgencyHomologations,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import {
  isDraftExpensable,
  isExpensableReporter,
  isManualExpensable,
  isOwnExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { trackDuplicateExpense } from 'modules/expenses/lib/expensables/expensableTracking'

import Accounting from './_Accounting'
import Alerts from './_Alerts'
import Comments from './_Comments'
import ConfirmationModals from './_ConfirmationModals'
import FilesPreviewer from './_FilesPreviewer'
import Form from './_Form'
import RecommendationSystem from './_RecommendationSystem'
import Sidepanel from './_Sidepanel'
import { BulkActionsProvider } from './hooks/useBulkActions'
import { useExpensableJournalEntries } from './hooks/useExpensableJournalEntries'
import { FilesPreviewerProvider, parseExpensableFiles } from './hooks/useFilesPreviewer'

interface ExpensableDetailProps {
  expensableId: string
  taxAgencyHomologationEnabled: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled?: boolean
  reportOnBehalfEnabled: boolean
  canUseSpendingPolicies?: boolean
  hasWorkflowSpendingPolicies?: boolean
  hasCostCentersFeature?: boolean
  canCreateExpensesForOthers?: boolean
  canOverrideApprovalFlow?: boolean
  canSetExpensableToPaid?: boolean
  canRestartApprovalFlow?: boolean
  canReviewExpensable?: boolean
  canSeeEmployeeLegalEntity: boolean
  canCustomizeSettings: boolean
  accountingTabEnabled: boolean
  projectsManagementEnabled: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  showRecommendationSystem?: boolean
  payrollData?: ExpensesRootPageQuery['payroll']
  compensationsData?: ExpensesRootPageQuery['compensations']
  compensationsEnabled?: boolean
  onClose: () => void
}

const ExpensableDetail = ({
  expensableId,
  taxAgencyHomologationEnabled,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled = false,
  reportOnBehalfEnabled,
  canUseSpendingPolicies = false,
  hasWorkflowSpendingPolicies = false,
  hasCostCentersFeature = false,
  canCreateExpensesForOthers = false,
  canOverrideApprovalFlow = false,
  canSetExpensableToPaid = false,
  canRestartApprovalFlow = false,
  canReviewExpensable = false,
  canSeeEmployeeLegalEntity,
  canCustomizeSettings,
  accountingTabEnabled,
  projectsManagementEnabled,
  currencies,
  showRecommendationSystem = false,
  payrollData,
  compensationsData,
  compensationsEnabled,
  onClose,
}: ExpensableDetailProps) => {
  const [hideAlert, toggleAlert] = useToggle(false)
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [currentTab, setCurrentTab] = useState('detail')

  const { employee: currentEmployee, company } = useCurrentSession()
  const companyId = company?.id

  const {
    expensable,
    payroll,
    budget,
    budgetAllocation,
    spendingPoliciesResult: spendingPolicyDataResult,
    loading,
    refetchExpensable,
    refetchSpendingPolicyEvaluate,
  } = useExpensableDetail(
    expensableId,
    reimburseThroughPayrollEnabled,
    canUseSpendingPolicies,
    hasWorkflowSpendingPolicies,
    payrollData,
    compensationsData,
    compensationsEnabled
  )

  const { data, loading: pageDetailsLoading } = useQuery(ExpenseDetailPageDocument, {
    variables: {
      expensableId: Number(expensableId),
      companyIdString: companyId?.toString(),
      companyId: companyId,
      reviewerIds: [currentEmployee.id],
    },
  })
  const spendingAlertsTarget =
    data?.expenses?.expensablesConnection?.edges[0]?.node?.spendingAlertsTarget || []
  const spendingAlertsSource =
    data?.expenses?.expensablesConnection?.edges[0]?.node?.spendingAlertsSource || []

  const approvalsV2Enabled =
    data?.features?.featuresConnection?.nodes.find(
      (f) => f.name === 'dev_expenses_approval_workflows'
    )?.enabled ?? false

  const approvalProcess =
    data?.expenses?.expensablesConnection?.edges[0]?.node?.approvalProcess || null

  const timelineEntries = data?.expenses?.approvalsTimelines?.[0]?.entries ?? []

  const spendingAlerts = [...spendingAlertsTarget, ...spendingAlertsSource]

  const [spendingPolicyEvaluationResult, setSpendingPolicyEvaluationResult] = useState<
    SpendingPolicesResultData | undefined
  >(spendingPolicyDataResult)

  const isDraftManual =
    expensable && isManualExpensable(expensable) && isDraftExpensable(expensable)
  const { alertCount, hasDuplicateAlerts } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: expensable?.amount,
    reimbursableAmount: expensable?.reimbursableAmount,
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    mileageRoundTrip: expensable?.mileage?.roundTrip ?? false,
    mileageCalculatedMileage: expensable?.mileage?.calculatedMileage ?? 0,
    mileageMileage: expensable?.mileage?.mileage ?? 0,
    spendingAlerts,
    hasWorkflowSpendingPolicies,
  })

  const { legalEntities } = useLegalEntities()
  // Always use the expense owner's legal entity for signing purposes,
  // not the current employee's (who might be an approver from a different country)
  const legalEntityId = expensable?.owner?.legalEntity?.id

  const signingLegalEntity = legalEntities.find((x) => x.id === legalEntityId)

  const { loading: loadingTaxAgencyHomologation, organizations: taxAgencyOrgs } =
    useTaxAgencyHomologations(signingLegalEntity ? [signingLegalEntity] : [])

  // Find the organization that matches the expense owner's legal entity country
  const organization = getOrganizationForLegalEntity(signingLegalEntity, taxAgencyOrgs)

  const signingOrganization =
    taxAgencyHomologationEnabled && organization?.applies ? organization.id : null

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }

  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }

  useUnsafeEffect(() => {
    if (expensable && hasDuplicateAlerts) {
      trackDuplicateExpense(expensable)
    }
  }, [expensable, hasDuplicateAlerts])

  useUnsafeEffect(() => {
    setSpendingPolicyEvaluationResult(spendingPolicyDataResult)
  }, [spendingPolicyDataResult])

  const initialFiles = useUnsafeMemo(() => {
    if (expensable) {
      return parseExpensableFiles(expensable)
    }
    return []
  }, [expensable]) //expensable is stable, only changed between graphql refetching

  const customizationFields = data?.expenses?.customizationFields || []
  const signedDocumentsTerms = data?.expenses?.signedDocumentsTermsConnection?.nodes ?? []
  const adeTermsAccepted = signedDocumentsTerms.some(
    (t) => t.organization === 'ade' && t.accepted === true
  )

  const canSeeBudgets = data?.finance._policies.budgetsSeeBudgets.can ?? false
  const canCreateBudgets = data?.finance._policies.budgetsCreateBudgets.can ?? false
  const canCreateSpendingPolicies = data?.finance?._policies?.spendingPolicyCreate?.can ?? false
  const canManageJournalEntries =
    data?.finance?._policies?.journalEntriesManagePurchase?.can ?? false

  const canEditExpensable =
    data?.expenses?.expensablesConnection?.edges[0]?.node?._policies?.edit?.can ?? false
  const canDeleteExpensable =
    data?.expenses?.expensablesConnection?.edges[0]?.node?._policies?.destroy?.can ?? false

  const isOwnAndDraft =
    expensable && isOwnExpensable(expensable, currentEmployee) && isDraftExpensable(expensable)
  const isReporterAndDraft =
    expensable && isExpensableReporter(expensable, currentEmployee) && isDraftExpensable(expensable)

  const isEditable =
    !expensable ||
    isOwnAndDraft ||
    isReporterAndDraft ||
    (isOwnExpensable(expensable, currentEmployee) && expensable.status === 'changes_requested') ||
    (canEditExpensable && isPendingExpensable(expensable))

  const anyLoading = loading || loadingTaxAgencyHomologation || pageDetailsLoading

  const { journalEntry, journalDraftEntry } = useExpensableJournalEntries({ expensable })
  const canSeeJournalEntries = canManageJournalEntries && (journalEntry || journalDraftEntry)

  // We have an action that is triggered in this level, but the consequences of this action need a form
  // context, so the actual implementation happens in a nested component. We are following a register pattern
  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  return (
    <AnimatePresence>
      <BulkActionsProvider expensable={expensable}>
        <Sidepanel
          expensable={expensable}
          open={openSidepanel}
          onClose={handleClose}
          onCloseClicked={handleCloseClicked}
          canRestartApprovalFlow={canRestartApprovalFlow}
          showTitle={!anyLoading}
        >
          {anyLoading && <Loading expand />}
          {!anyLoading && expensable && (
            <FilesPreviewerProvider initialFiles={initialFiles}>
              {openFilePreviewer && (
                <FilesPreviewer
                  onClose={handleClose}
                  onDrop={onDropHandler}
                  showUploadButton={isEditable}
                />
              )}
              <ConfirmationModals expensable={expensable} payroll={payroll} />
              <Tabs
                currentId={currentTab}
                tabsAlign="flexStart"
                onTabClicked={setCurrentTab}
                hasAdditionalPadding
              >
                <Tabs.Tab id="detail" label={i18n.t('expenses_v2.modals.detail.tabs.summary')}>
                  {showRecommendationSystem &&
                    expensable.type === 'expense' &&
                    expensable.expense?.id &&
                    expensable.expense?.category?.id &&
                    ['restaurants', 'accommodation'].includes(expensable.expense.category.id) && (
                      <RecommendationSystem expenseId={expensable.expense.id} />
                    )}
                  <Form
                    expensable={expensable}
                    taxAgencyOrganization={signingOrganization}
                    customizationFields={customizationFields}
                    adeTermsAccepted={adeTermsAccepted}
                    onClose={handleClose}
                    hasReportOnBehalfFeature={reportOnBehalfEnabled}
                    hasProjectsFeature={projectsManagementEnabled}
                    reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
                    reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                    refetchExpensable={refetchExpensable}
                    refetchSpendingPolicyEvaluate={refetchSpendingPolicyEvaluate}
                    setSpendingPolicyEvaluationResult={setSpendingPolicyEvaluationResult}
                    canUseSpendingPolicies={canUseSpendingPolicies}
                    canCreateExpensesForOthers={canCreateExpensesForOthers}
                    canOverrideApprovalFlow={canOverrideApprovalFlow}
                    canSetExpensableToPaid={canSetExpensableToPaid}
                    canReviewExpensable={canReviewExpensable}
                    canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                    canCustomizeSettings={canCustomizeSettings}
                    isEditable={isEditable}
                    canDeleteExpensable={canDeleteExpensable}
                    currencies={currencies}
                    onClickSnackBar={() => setCurrentTab('alerts')}
                    toggleAlert={toggleAlert}
                    hideAlert={hideAlert}
                    budget={budget}
                    budgetAllocation={budgetAllocation}
                    spendingPolicyEvaluationResult={spendingPolicyEvaluationResult}
                    spendingAlerts={spendingAlerts}
                    registerDropHandler={setOnDropHandler}
                    hasWorkflowSpendingPolicies={hasWorkflowSpendingPolicies}
                    hasCostCentersFeature={hasCostCentersFeature}
                    approvalsV2Enabled={approvalsV2Enabled}
                    approvalProcess={approvalProcess}
                    timelineEntries={timelineEntries}
                  />
                </Tabs.Tab>
                <Tabs.Tab id="comments" label={i18n.t('expenses_v2.modals.detail.tabs.comments')}>
                  <Comments expensableId={expensable.id} />
                </Tabs.Tab>
                {canUseSpendingPolicies ? (
                  <Tabs.Tab
                    id="alerts"
                    label={
                      alertCount > 0
                        ? `${i18n.t('expenses_v2.modals.detail.tabs.alerts')} (${alertCount})`
                        : i18n.t('expenses_v2.modals.detail.tabs.alerts')
                    }
                  >
                    <Alerts
                      expensable={expensable}
                      budget={budget}
                      budgetAllocation={budgetAllocation}
                      policiesResultData={spendingPolicyEvaluationResult}
                      canSeeBudgets={canSeeBudgets}
                      canCreateBudgets={canCreateBudgets}
                      canCreateSpendingPolicies={canCreateSpendingPolicies}
                      spendingAlerts={spendingAlerts}
                      hasWorkflowSpendingPolicies={hasWorkflowSpendingPolicies}
                      canOverrideApprovalFlow={canOverrideApprovalFlow}
                    />
                  </Tabs.Tab>
                ) : (
                  <></>
                )}

                {accountingTabEnabled && canSeeJournalEntries ? (
                  <Tabs.Tab
                    id="accounting"
                    label={i18n.t('expenses_v2.modals.detail.tabs.accounting')}
                  >
                    <Accounting expensable={expensable} />
                  </Tabs.Tab>
                ) : (
                  <></>
                )}
              </Tabs>
            </FilesPreviewerProvider>
          )}
          {!anyLoading && !expensable && (
            <BlankSlate
              illustration="sign-post"
              title={i18n.t('errors.page_not_found.title')}
              description={i18n.t('errors.page_not_found.description')}
            />
          )}
        </Sidepanel>
      </BulkActionsProvider>
    </AnimatePresence>
  )
}

export default observer(ExpensableDetail)
